import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";

import { type Editor } from "@tiptap/core";

import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { StarterKit } from "@tiptap/starter-kit";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { UiState } from "@/components/tiptap-extension/ui-state-extension";

import {
  Close,
  FormatListNumbered,
  PlayArrow,
  Stop,
  History,
  Undo,
  Redo,
  VideoLibrary,
  Help,
  ContentPaste,
  KeyboardTab,
  CloudUpload,
} from "@mui/icons-material";
import { type TiptapCollabProvider } from "@tiptap-pro/provider";
import { Collaboration } from "@tiptap/extension-collaboration";
import type * as Y from "yjs";

import { type TestEditorPropertiesType } from "@/atoms/test-editor-atom";
import {
  calculateTotalWeight,
  CustomMention,
  getPromptStepsFromTipTapNode,
} from "@/utils/helpers";
import { Snapshot, watchPreviewContent } from "@tiptap-pro/extension-snapshot";
import { DateTime } from "luxon";
import { type SnapshotVersion } from "@tiptap-pro/extension-snapshot";
import { EditorState } from "prosemirror-state";
import {
  type AudioFile,
  type BuildData,
  type PromptStep,
  type RunExecutionData,
  type SettingsData,
  type TemplateWithLabel,
  type TestTemplate,
} from "@/utils/types";
import { CollaborationCaret } from "@tiptap/extension-collaboration-caret";
import { createCustomCommand } from "./command-suggestions/commands";
import SuggestionsBuilder from "./command-suggestions/suggestions-builder";
import UniqueID from "@tiptap/extension-unique-id";
import { atom, useSetAtom } from "jotai";
import { useLocation } from "react-router";
import LoadingButton from "@mui/lab/LoadingButton";
import { type UseMutationResult } from "@tanstack/react-query/src/types";
import {
  tiptapAudiosSuggestions,
  tiptapDoubleCurlySuggestions,
  tiptapDynamicCommandsSuggestions,
  tiptapTemplatesSuggestions,
} from "@/constants/suggestions-constants";

const darkModeColors = [
  "#B94E4E",
  "#B97D4E",
  "#A4A35A",
  "#4287A8",
  "#4E9177",
  "#6D8E47",
];
const randomDarkModeColor = () =>
  darkModeColors[Math.floor(Math.random() * darkModeColors.length)];

const TipTapEditor = ({
  testEditorProperties,
  setTestCommandsUpdated,
  testCommandsUpdated,
  stopExecution,
  running,
  setTestEditorProperties,
  provider,
  document,
  userEmail,
  acceptedDynamicValues,
  templates,
  isNewTest,
  auxDisplayPasteStep = false,
  setAuxDisplayPasteStep,
  selectedTestDependencyId,
  executeMutation,
  executeWithDependenciesMutation,
  dependencies,
  settingsData,
  audios,
  buildData,
  markdownMode = false,
  onImportClick,
  onSwitchToStandardEditor,
}: {
  testEditorProperties: TestEditorPropertiesType;
  setTestCommandsUpdated: Dispatch<SetStateAction<boolean>>;
  testCommandsUpdated: boolean;
  executeMutation: UseMutationResult<void, Error, void | PromptStep[], unknown>;
  executeWithDependenciesMutation: UseMutationResult<
    RunExecutionData[],
    Error,
    void | PromptStep,
    unknown
  >;
  running: boolean;
  stopExecution: () => void;
  setTestEditorProperties: Dispatch<SetStateAction<TestEditorPropertiesType>>;
  provider: TiptapCollabProvider;
  document: Y.Doc;
  userEmail: string;
  acceptedDynamicValues: string[];
  templates: TemplateWithLabel[];
  audios: AudioFile[];
  isNewTest: boolean;
  auxDisplayPasteStep: boolean;
  setAuxDisplayPasteStep: Dispatch<SetStateAction<boolean>>;
  selectedTestDependencyId?: string;
  dependencies?: TestTemplate[];
  settingsData: SettingsData;
  buildData: BuildData;
  markdownMode?: boolean;
  onImportClick?: () => void;
  onSwitchToStandardEditor?: () => void;
}) => {
  const [versions, setVersions] = React.useState<SnapshotVersion[]>([]);
  const [versioningModalOpen, setVersioningModalOpen] = React.useState(false);
  const [dynamicContentWarning, setDynamicContentWarning] = React.useState("");
  const setMainTipTapEditorAtom = useSetAtom(mainTipTapEditorAtom);
  const location = useLocation();
  const [displayPasteStep, setDisplayPasteStep] = useState(false);
  const [markdownHintDismissed, setMarkdownHintDismissed] = useState(false);

  const templatesRef = useRef(templates);
  const audiosRef = useRef(audios);
  const dependenciesRef = useRef(dependencies);

  useEffect(() => {
    templatesRef.current = templates;
  }, [templates]);

  useEffect(() => {
    audiosRef.current = audios;
  }, [audios]);

  useEffect(() => {
    dependenciesRef.current = dependencies;
  }, [dependencies]);

  const editor = useEditor({
    shouldRerenderOnTransaction: false,
    immediatelyRender: false,
    onCreate: ({ editor }) => {
      setMainTipTapEditorAtom(editor);
      const urlSearchParams = new URLSearchParams(location.search);

      const matchParam = urlSearchParams.get("match");
      if (matchParam) {
        const decodedMatch = decodeURIComponent(matchParam);
        setTimeout(() => {
          const node = editor
            .$nodes("paragraph")
            ?.find((nodePos) => nodePos.textContent.includes(decodedMatch));
          if (node != null) {
            const nodeTextContent = node.textContent;
            editor.commands.setTextSelection({
              from:
                node.from +
                nodeTextContent
                  .toLowerCase()
                  .indexOf(decodedMatch.toLowerCase()),
              to:
                node.from +
                nodeTextContent
                  .toLowerCase()
                  .indexOf(decodedMatch.toLowerCase()) +
                decodedMatch.length,
            });
            editor.commands.focus();
          }
        }, 700);
      } else {
        const selectedNodeParam = urlSearchParams.get("selectedNode");

        if (selectedNodeParam != null) {
          setTimeout(() => {
            const node = editor
              .$nodes("paragraph")
              ?.find((nodePos) => nodePos.attributes.id === selectedNodeParam);
            if (node != null) {
              editor.commands.setTextSelection({
                from: node.from,
                to: node.to,
              });
              editor.commands.focus();
            }
            setDisplayPasteStep(true);
          }, 700);
        }
      }

      if (isNewTest) {
        editor.commands.setContent(
          testEditorProperties.originalTestCommands.toJSON()
        );
        setTestEditorProperties((prev) => ({
          ...prev,
          testCommands: testEditorProperties.originalTestCommands,
        }));
        setTestCommandsUpdated(false);
      }
    },
    onUpdate: ({ editor }) => {
      setTestEditorProperties((prev) => ({
        ...prev,
        testCommands: editor.state.doc,
      }));

      const originalTestCommandsJSON =
        testEditorProperties.originalTestCommands.content.toJSON();
      if (originalTestCommandsJSON != null) {
        setTestCommandsUpdated(
          JSON.stringify(originalTestCommandsJSON) !==
            JSON.stringify(editor.state.doc.content.toJSON())
        );
      }

      const plainText = editor.state.doc.textContent;
      const tentativeTemplates = [...(plainText.match(/{{(.*?)}}/g) ?? [])].map(
        (match) => match.replace(/{{|}}/g, "")
      );

      let shouldDisplayExtractLinkWarning = false;
      if (tentativeTemplates.includes("extractedLink")) {
        const extractedLinkPosition = plainText.lastIndexOf("extractedLink");
        const reducedPlainText = plainText.slice(0, extractedLinkPosition);
        const extractLinkFromEmailCommandFound =
          /extractLinkFromEmail:\s*([^;]+);(.+)/.test(reducedPlainText);

        if (!extractLinkFromEmailCommandFound) {
          shouldDisplayExtractLinkWarning = true;
        }
      }
      const dynamicVariablesMissing = [
        ...new Set(
          tentativeTemplates.filter(
            (key) =>
              !acceptedDynamicValues.includes(key.trim()) &&
              !key.match(/randomNumber\((-?\d+)\)/) &&
              !key.match(/randomNumberInRange\((-?\d+),(-?\d+)\)/) &&
              !key.match(/datetime\(([+-]?\d+(?:\.\d+)?)\)/) &&
              key !== "extractedLink"
          )
        ),
      ];
      if (dynamicVariablesMissing.length > 0) {
        const steps = getPromptStepsFromTipTapNode({
          node: editor.state.doc.toJSON(),
        });

        const nonRememberedValues = dynamicVariablesMissing.filter((value) => {
          const templateUsage = `{{${value}}}`;
          // Find which step index uses this variable
          const usageStepIndex = steps.findIndex((step) =>
            step.text.includes(templateUsage)
          );
          if (usageStepIndex === -1) return true;

          // Check if any previous step defined this variable
          const rememberedPattern = new RegExp(
            `(as\\s+(variable\\s+)?|variable\\s+)${value}\\b`,
            "i"
          );
          const previousSteps = steps.slice(0, usageStepIndex);
          return !previousSteps.some((step) =>
            rememberedPattern.test(step.plainText)
          );
        });

        if (nonRememberedValues.length === 0) {
          setDynamicContentWarning("");
          return;
        }

        const justOne = nonRememberedValues.length === 1;
        if (justOne) {
          setDynamicContentWarning(
            `Variable ${nonRememberedValues.join(", ")} is not defined!`
          );
        } else {
          setDynamicContentWarning(
            `Variables ${nonRememberedValues.join(", ")} are not defined!`
          );
        }
      } else if (shouldDisplayExtractLinkWarning) {
        setDynamicContentWarning(
          "There should be a `extractLinkFromEmail` command before trying to access the `extractedLink` value"
        );
      } else {
        setDynamicContentWarning("");
      }
    },
    editorProps: {
      attributes: {
        class:
          `flex-1 max-w-none w-full prose prose-invert focus:outline-none h-[360px] overflow-auto px-3 py-1 prose-p:m-0 ${
            markdownMode
              ? "prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h1:mt-8 prose-h2:mt-7 prose-h3:mt-6 prose-h1:mb-2 prose-h2:mb-2 prose-h3:mb-1"
              : ""
          }`,
      },
    },
    extensions: [
      UiState,
      StarterKit.configure({
        undoRedo: false, // important because history will now be handled by Y.js
        trailingNode: false,
        listKeymap: false,
        link: false,
      }),
      Collaboration.configure({
        document,
      }),
      UniqueID.configure({
        types: ["paragraph", "mention"],
      }),
      CollaborationCaret.configure({
        provider,
        user: {
          name: userEmail,
          color: randomDarkModeColor(),
        },
      }),
      Snapshot.configure({
        provider,
        onUpdate: (data) => {
          if (data.versioningEnabled) {
            editor?.commands.toggleVersioning();
          }
          setVersions(data.versions);
        },
      }),
      // `{{...}}` dynamic-value placeholders (timestamp, currentDate,
      // randomNumber, env vars, etc.) are resolved at runtime in both the
      // standard and markdown editors, so this suggestion stays enabled
      // regardless of `markdownMode` as long as the company has command
      // suggestions turned on.
      ...(settingsData.enableCommandSuggestions === true
        ? [
            createCustomCommand({ name: "envVariablesCommand" }).configure({
              suggestion: SuggestionsBuilder({
                chars: ["{{"],
                startOfLine: false,
                oneCharTrigger: false,
                allowedPrefixes: null,
                items: (props) => {
                  const query = props.query.replace("{{", "");
                  return tiptapDoubleCurlySuggestions(
                    acceptedDynamicValues
                  ).sort((a, b) => {
                    const weightA = calculateTotalWeight(a.alternatives, query);
                    const weightB = calculateTotalWeight(b.alternatives, query);

                    // Sort in descending order of weights
                    return weightB - weightA;
                  });
                },
              }),
            }),
          ]
        : []),
      ...(settingsData.enableCommandSuggestions === true && !markdownMode
        ? [
            createCustomCommand({ name: "customCommand" }).configure({
              suggestion: SuggestionsBuilder({
                // array with all lowercase and uppercase letters
                chars: [...Array(26)].flatMap((_, i) => [
                  String.fromCharCode(i + 97),
                  String.fromCharCode(i + 65),
                ]),
                breakRegexWords: ["mpl", "lay"],
                items: (props) => {
                  const query = props.query.toLowerCase();
                  return tiptapDynamicCommandsSuggestions(settingsData).sort(
                    (a, b) => {
                      const weightA = calculateTotalWeight(
                        a.alternatives,
                        query
                      );
                      const weightB = calculateTotalWeight(
                        b.alternatives,
                        query
                      );

                      // Sort in descending order of weights
                      return weightB - weightA;
                    }
                  );
                },
              }),
            }),
            createCustomCommand({ name: "templateCommand" }).configure({
              suggestion: SuggestionsBuilder({
                chars: ["tmpl"],
                startOfLine: false,
                oneCharTrigger: false,
                items: (props) => {
                  const query = props.query;
                  return tiptapTemplatesSuggestions(templatesRef.current).sort(
                    (a, b) => {
                      const weightA = calculateTotalWeight(
                        a.alternatives,
                        query
                      );
                      const weightB = calculateTotalWeight(
                        b.alternatives,
                        query
                      );

                      // Sort in descending order of weights
                      return weightB - weightA;
                    }
                  );
                },
              }),
            }),
            createCustomCommand({ name: "audioCommand" }).configure({
              suggestion: SuggestionsBuilder({
                chars: ["play"],
                startOfLine: false,
                oneCharTrigger: false,
                items: (props) => {
                  const query = props.query;
                  return tiptapAudiosSuggestions(audiosRef.current).sort(
                    (a, b) => {
                      const weightA = calculateTotalWeight(
                        a.alternatives,
                        query
                      );
                      const weightB = calculateTotalWeight(
                        b.alternatives,
                        query
                      );

                      // Sort in descending order of weights
                      return weightB - weightA;
                    }
                  );
                },
              }),
            }),
            ...(settingsData.enableTestMentions
              ? [
                  CustomMention.configure({
                    HTMLAttributes: {
                      class: "mention",
                    },
                    renderHTML({ options, node }) {
                      return [
                        "a",
                        {
                          ...options.HTMLAttributes,
                          class: "mention",
                          href: `/gpt-driver/build/${buildData.id}/test/${node.attrs.testId}`,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          "data-test-id": node.attrs.testId,
                        },
                        `@${node.attrs.label}`,
                      ];
                    },
                    suggestion: SuggestionsBuilder({
                      chars: ["@"],
                      startOfLine: false,
                      oneCharTrigger: false,
                      items: (props) => {
                        const query = props.query.toLowerCase();
                        return [
                          ...(dependenciesRef.current?.map((dependency) => ({
                            title: dependency.title,
                            alternatives: [dependency.title.toLowerCase()],
                            label: dependency.title.toLowerCase(),
                            testId: dependency.id,
                            autoId: true,
                          })) ?? []),
                        ].sort((a, b) => {
                          const weightA = calculateTotalWeight(
                            a.alternatives,
                            query
                          );
                          const weightB = calculateTotalWeight(
                            b.alternatives,
                            query
                          );

                          // Sort in descending order of weights
                          return weightB - weightA;
                        });
                      },
                    }),
                  }),
                ]
              : []),
          ]
        : []),
    ],
  });
  const selectionEmpty = useEditorState({
    editor,
    selector: (ctx) => ctx.editor?.state.selection.empty ?? true,
  });

  const docIsEmpty = useEditorState({
    editor,
    selector: (ctx) =>
      (ctx.editor?.state.doc.textContent ?? "").trim().length === 0,
  });

  useEffect(() => {
    if (auxDisplayPasteStep && selectionEmpty === false) {
      setDisplayPasteStep(true);
    }
  }, [auxDisplayPasteStep, selectionEmpty]);

  useEffect(() => {
    if (displayPasteStep && selectionEmpty) {
      setDisplayPasteStep(false);
      setAuxDisplayPasteStep(false);
    }
  }, [
    selectionEmpty,
    displayPasteStep,
    auxDisplayPasteStep,
    setAuxDisplayPasteStep,
  ]);

  useEffect(() => {
    if (editor != null) {
      const origintalTestCommandsJSON =
        testEditorProperties.originalTestCommands.content.toJSON();
      if (origintalTestCommandsJSON != null) {
        setTestCommandsUpdated(
          JSON.stringify(origintalTestCommandsJSON) !==
            JSON.stringify(editor.state.doc.content.toJSON())
        );
      }
    }
  }, [
    testEditorProperties.originalTestCommands,
    editor,
    setTestCommandsUpdated,
  ]);

  const resetEditorContent = useCallback(() => {
    if (editor != null) {
      // Capture the current selection
      const currentSelection = editor.state.selection;

      // Reset the content
      editor.commands.setContent(editor.getJSON());

      // Create a new editor state while preserving the old selection
      const newEditorState = EditorState.create({
        doc: editor.state.doc,
        plugins: editor.state.plugins,
        selection: currentSelection,
      });

      // Update the editor state
      editor.view.updateState(newEditorState);
    }
  }, [editor]);

  const handleClosePasteStepChip = useCallback(() => {
    setDisplayPasteStep(false);
  }, []);

  const handlePasteStep = useCallback(async () => {
    const urlSearchParams = new URLSearchParams(location.search);
    const selectedNodeParam = urlSearchParams.get("selectedNode");
    const pasteContent = await navigator.clipboard.readText();

    if (selectedNodeParam != null) {
      const node = editor
        .$nodes("paragraph")
        ?.find((nodePos) => nodePos.attributes.id === selectedNodeParam);
      const pasteContent = await navigator.clipboard.readText();
      if (node != null) {
        editor
          .chain()
          .focus()
          .insertContentAt({ from: node.from, to: node.to }, pasteContent)
          .run();
      }
    } else if (editor?.state.selection != null && auxDisplayPasteStep) {
      editor
        .chain()
        .focus()
        .insertContentAt(
          {
            from: editor.state.selection.from,
            to: editor.state.selection.to,
          },
          pasteContent
        )
        .run();
    }
  }, [editor, location, auxDisplayPasteStep]);

  const handleVersioningClose = useCallback(() => {
    setVersioningModalOpen(false);
  }, []);

  const handleRevert = useCallback(
    (version, versionData) => {
      const versionTitle = versionData
        ? versionData.name || versionData.date.toString()
        : version;

      editor.commands.revertToVersion(version, versionTitle, versionTitle);

      resetEditorContent();
    },
    [editor, resetEditorContent]
  );

  const openVersioningModal = useCallback(() => {
    setVersioningModalOpen(true);
  }, []);

  const handleDismiss = useCallback(() => {
    editor.commands.saveVersion(userEmail);
    editor?.commands.setContent(
      testEditorProperties.originalTestCommands.toJSON()
    );
    editor.commands.saveVersion(userEmail);
    resetEditorContent();
  }, [editor, resetEditorContent, userEmail, testEditorProperties]);

  return (
    <>
      <VersioningDialog
        versions={versions}
        provider={provider}
        isOpen={versioningModalOpen}
        handleClose={handleVersioningClose}
        onRevert={handleRevert}
      />
      <EditorToolBar
        stopExecution={stopExecution}
        running={running}
        selectedTestDependencyId={selectedTestDependencyId}
        editor={editor}
        openVersioningModal={openVersioningModal}
        versions={versions}
        markdownMode={markdownMode}
        onImportClick={onImportClick}
        onSwitchToStandardEditor={onSwitchToStandardEditor}
        executeMutation={executeMutation}
        executeWithDependenciesMutation={executeWithDependenciesMutation}
      />
      {testCommandsUpdated && (
        <Alert
          severity="warning"
          className={"mt-2.5"}
          action={
            <Button color="inherit" size="small" onClick={handleDismiss}>
              Restore Saved Version
            </Button>
          }
        >
          This is an unsaved prompt
        </Alert>
      )}
      {markdownMode && docIsEmpty && !markdownHintDismissed && (
        <Alert
          severity="info"
          className="mt-2.5"
          onClose={() => setMarkdownHintDismissed(true)}
        >
          Paste any test instructions here, then press Play. <br/> We will automatically optimize the format if needed.
        </Alert>
      )}
      <div className="relative">
        <BubbleMenu
          editor={editor}
          options={{ duration: 100, placement: "top-start" }}
        >
          {displayPasteStep && (
            <Chip
              icon={<ContentPaste />}
              label="Replace with copied prompt"
              variant="filled"
              sx={{
                backgroundColor: "#616161",
                "&:hover": {
                  backgroundColor: "#616161",
                },
              }}
              onClick={handlePasteStep}
              onDelete={handleClosePasteStepChip}
            />
          )}
        </BubbleMenu>
        <EditorContent editor={editor} role="presentation" />
      </div>
      {dynamicContentWarning.length > 0 && (
        <Alert severity="error" className={"mt-2.5"}>
          {dynamicContentWarning}
        </Alert>
      )}
    </>
  );
};
const VersioningDialog = ({
  versions,
  isOpen,
  handleClose,
  onRevert,
  provider,
}: {
  versions: SnapshotVersion[];
  isOpen: boolean;
  handleClose: () => void;
  onRevert: (version: number, versionData: SnapshotVersion) => void;
  provider: TiptapCollabProvider;
}) => {
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const isCurrentVersion =
    versions && versions.length > 0
      ? currentVersionId === versions.at(-1).version
      : false;

  const editor = useEditor({
    editable: false,
    content: "",
    extensions: [
      StarterKit.configure({ trailingNode: false }),
      CustomMention.configure({ HTMLAttributes: { class: "mention" } }),
    ],
    editorProps: {
      attributes: {
        class:
          "flex-1 max-w-none w-0 prose prose-invert focus:outline-none overflow-auto p-3 prose-p:m-0",
      },
    },
  });

  const closeHandler = useCallback(() => {
    handleClose();
    setCurrentVersionId(null);
    editor.commands.clearContent();
  }, [handleClose, editor]);

  const reversedVersions = useMemo(
    () => versions.slice().reverse(),
    [versions]
  );

  const handleVersionChange = useCallback(
    (newVersion) => {
      setCurrentVersionId(newVersion);

      provider.sendStateless(
        JSON.stringify({
          action: "version.preview",
          version: newVersion,
        })
      );
    },
    [provider]
  );

  const versionData = useMemo(() => {
    if (!versions.length) {
      return null;
    }

    return versions.find((v) => v.version === currentVersionId);
  }, [currentVersionId, versions]);

  useEffect(() => {
    if (isOpen && currentVersionId === null && versions.length > 0) {
      const initialVersion = versions.at(-1).version;

      setCurrentVersionId(initialVersion);

      provider.sendStateless(
        JSON.stringify({
          action: "version.preview",
          version: initialVersion,
        })
      );
    }
  }, [currentVersionId, versions, isOpen, provider]);

  useEffect(() => {
    if (isOpen) {
      const unbindContentWatcher = watchPreviewContent(provider, (content) => {
        if (editor) {
          editor.commands.setContent(content);
        }
      });

      return () => {
        unbindContentWatcher();
      };
    }
  }, [isOpen, provider, editor]);

  const handleRevert = useCallback(() => {
    const accepted = confirm("Are you sure you want to restore this version?"); // eslint-disable-line no-restricted-globals

    if (accepted) {
      onRevert(currentVersionId!, versionData!);
      closeHandler();
    }
  }, [onRevert, currentVersionId, closeHandler, versionData]);

  const getVersionName = useCallback((version: SnapshotVersion) => {
    if (version.name) {
      return version.name;
    }

    if (version.version === 0) {
      return "Initial version";
    }

    return `Version ${version.version}`;
  }, []);
  return (
    <Dialog fullWidth maxWidth={"lg"} open={isOpen} onClose={closeHandler}>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>Available versions</Box>
          <Box>
            <IconButton onClick={closeHandler}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={"m-0 p-0"} sx={{ padding: 0 }}>
        <div className={"flex w-full divide-x divide-[#ffffff1f] h-96"}>
          <div className={"flex-[2_2_0%] h-full overflow-auto"}>
            <EditorContent className={"flex"} editor={editor} />
          </div>
          <div
            className={
              "flex flex-col flex-[1_1_0%] h-full items-center divide-y divide-[#ffffff1f] w-full"
            }
          >
            <div
              className={
                "flex flex-col flex-[5_5_0%] overflow-auto w-full items-center gap-2"
              }
            >
              {reversedVersions.map((v) => (
                <VersionItem
                  date={v.date}
                  title={getVersionName(v)}
                  onClick={() => {
                    handleVersionChange(v.version);
                  }}
                  isActive={currentVersionId === v.version}
                  key={`version_item_${v.version}`}
                />
              ))}
            </div>
            <div className={"flex justify-center flex-[1_1_0%] w-full p-4"}>
              <Button
                variant={"contained"}
                onClick={handleRevert}
                disabled={!versionData || isCurrentVersion}
              >
                Restore this version
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VersionItem = ({ title, date, isActive, onClick }) => {
  return (
    <Button
      disabled={isActive}
      onClick={onClick}
      variant={isActive ? "contained" : "text"}
      className={"flex flex-col first:mt-1 last:mb-1"}
      sx={{
        fontSize: "10px",
      }}
    >
      <span className="version-name">{title}</span>
      {title ? (
        <span className="version-date">
          {DateTime.fromMillis(date).toFormat("yyyy-MM-dd hh:mm:ss a")}
        </span>
      ) : null}
    </Button>
  );
};

const EditorToolBar = ({
  running,
  stopExecution,
  editor,
  versions,
  openVersioningModal,
  selectedTestDependencyId,
  executeMutation,
  executeWithDependenciesMutation,
  markdownMode = false,
  onImportClick,
  onSwitchToStandardEditor,
}: {
  running: boolean;
  stopExecution: () => void;
  editor: Editor | null;
  openVersioningModal: () => void;
  versions: CollabHistoryVersion[];
  executeMutation: UseMutationResult<void, Error, void | PromptStep[], unknown>;
  executeWithDependenciesMutation: UseMutationResult<
    RunExecutionData[],
    Error,
    void | PromptStep,
    unknown
  >;
  selectedTestDependencyId?: string;
  markdownMode?: boolean;
  onImportClick?: () => void;
  onSwitchToStandardEditor?: () => void;
}) => {
  const { canUndo, canRedo, isSelectionEmpty } = useEditorState({
    editor,
    selector: (ctx) => ({
      canUndo: ctx.editor?.can().undo() ?? false,
      canRedo: ctx.editor?.can().redo() ?? false,
      isSelectionEmpty: ctx.editor?.state.selection.empty ?? true,
    }),
  }) ?? { canUndo: false, canRedo: false, isSelectionEmpty: true };

  const executeSelection = useCallback(() => {
    const promptSteps = getPromptStepsFromTipTapNode({
      node: editor!.state.doc,
      selection: editor!.state.selection,
    });

    executeMutation.mutate(promptSteps);
  }, [editor?.state, executeMutation]);

  const executeWithDependenciesHandler = useCallback(() => {
    executeWithDependenciesMutation.mutate();
  }, [executeWithDependenciesMutation]);

  const executeWithDependenciesWithUpperLimitHandler = useCallback(async () => {
    await Notification.requestPermission();

    const promptSteps = getPromptStepsFromTipTapNode({
      node: editor!.state.doc,
      selection: editor!.state.selection,
    });

    executeWithDependenciesMutation.mutate(promptSteps.at(-1));
  }, [executeWithDependenciesMutation]);

  const executeWithoutDependencies = useCallback(() => {
    executeMutation.mutate();
  }, [executeMutation]);

  const isPreparingOrRunning =
    running ||
    executeWithDependenciesMutation.isPending ||
    executeMutation.isPending;

  return (
    <div className={"flex justify-between"}>
      <div className={"flex px-2 items-center gap-0.5"}>
        <Tooltip title={"Undo"}>
          <span>
            <IconButton
              aria-label="undo"
              size="medium"
              disabled={!canUndo}
              onClick={() => {
                editor!.commands.undo();
              }}
            >
              <Undo />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={"Redo"}>
          <span>
            <IconButton
              aria-label="redo"
              size="medium"
              disabled={!canRedo}
              onClick={() => {
                editor!.commands.redo();
              }}
            >
              <Redo />
            </IconButton>
          </span>
        </Tooltip>
        {!markdownMode && (
          <Tooltip title={"Ordered list"}>
            <IconButton
              aria-label="format-list-number"
              size="medium"
              onClick={() => {
                editor!.chain().focus().toggleOrderedList().run();
              }}
            >
              <FormatListNumbered />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={"Check versions"}>
          <span>
            <IconButton
              disabled={versions.length === 0}
              aria-label="versions"
              size="small"
              onClick={openVersioningModal}
            >
              <History />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={"Best Practices"}>
          <IconButton
            target="_blank"
            aria-label="best practices"
            size="medium"
            href={"https://docs.mobileboost.io"}
          >
            <Help />
          </IconButton>
        </Tooltip>
      </div>
      <div className={"flex gap-1.5 pr-2.5 items-stretch"}>
        {!isSelectionEmpty ? (
          <>
            {selectedTestDependencyId != null && !isPreparingOrRunning && (
              <Tooltip
                title={"Execute until this step, including dependencies"}
              >
                <span>
                  <Button
                    size="medium"
                    variant="contained"
                    onClick={executeWithDependenciesWithUpperLimitHandler}
                    disabled={executeWithDependenciesMutation.isPending}
                    sx={{
                      minWidth: 0,
                      height: "100%",
                      "& .MuiButton-startIcon": { margin: 0 },
                    }}
                  >
                    <KeyboardTab />
                  </Button>
                </span>
              </Tooltip>
            )}

            {!isPreparingOrRunning && (
              <Button
                variant="contained"
                size="medium"
                startIcon={<PlayArrow />}
                onClick={executeSelection}
              >
                Run selected
              </Button>
            )}
          </>
        ) : (
          <>
            {selectedTestDependencyId != null && !isPreparingOrRunning && (
              <Tooltip title="Execute prompt incl. dependencies">
                <span>
                  <Button
                    id={
                      running
                        ? "stop-with-dependencies"
                        : "play-with-dependencies"
                    }
                    onClick={executeWithDependenciesHandler}
                    variant={"contained"}
                    size="medium"
                    disabled={
                      running && !executeWithDependenciesMutation.isPending
                    }
                    sx={{ height: "100%" }}
                  >
                    <VideoLibrary />
                  </Button>
                </span>
              </Tooltip>
            )}
            {!isPreparingOrRunning && (
              <Tooltip title="Execute prompt">
                <span>
                  <Button
                    variant={"contained"}
                    size="medium"
                    onClick={executeWithoutDependencies}
                    sx={{ height: "100%" }}
                  >
                    <PlayArrow />
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}
        {isPreparingOrRunning && (
          <LoadingButton
            variant="contained"
            size="medium"
            startIcon={<Stop />}
            onClick={stopExecution}
            loadingPosition={"start"}
            loading={
              executeWithDependenciesMutation.isPending ||
              executeMutation.isPending
            }
          >
            {executeWithDependenciesMutation.isPending ||
            executeMutation.isPending
              ? "Preparing data"
              : "Stop Execution"}
          </LoadingButton>
        )}
      </div>
    </div>
  );
};

const mainTipTapEditorAtom = atom<Editor | null>(null);

export default TipTapEditor;
export { mainTipTapEditorAtom };
